import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import { type ITagGroup } from "@medoma/models/templates";
import { dateSchema } from "@medoma/models/date-and-time";
import type { SiteId } from "@medoma/models/sites";
import {
  templateFormWithoutPatientSchema,
  templateFormWithPatientSchema,
} from "@/forms/AddActivity/templateFormSchema";
import { batchTemplateFormSchema } from "@/forms/AddActivity/batchTemplateFormSchema";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Query keys
 */

const templatesKeys = {
  all: ["templates"] as const,
  forSite: (siteId: SiteId) => [...templatesKeys.all, { siteId }] as const,
};

const tagsKeys = {
  all: ["templates", "tags"] as const,
  forSite: (siteId: SiteId) => [...tagsKeys.all, { siteId }] as const,
  group: (siteId: SiteId, group: ITagGroup) =>
    [...tagsKeys.forSite(siteId), group] as const,
};

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Schemas and types
 */

const templateTagSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  updatedAt: dateSchema.nullish(),
  deletedAt: dateSchema.nullish(),
});

const templateBaseSchema = z.object({
  templateId: z.uuid(),
  revisionNumber: z.number().int().positive(),
  tags: z.array(templateTagSchema),
  updatedAt: dateSchema,
});

// The backend nests the template itself as a JSON string inside the envelope.
// Only that string is pinned here so the envelope and the content are decoded
// together, in one pass, once the string has been JSON.parsed.
const backendTemplateSchema = z.looseObject({
  template: z.string(),
});

export const activityTemplateWithPatientSchema = templateBaseSchema.extend({
  template: templateFormWithPatientSchema,
});

const activityTemplateWithoutPatientSchema = templateBaseSchema.extend({
  template: templateFormWithoutPatientSchema,
});

const activityTemplateSchema = z.union([
  activityTemplateWithPatientSchema,
  activityTemplateWithoutPatientSchema,
]);

export const batchTemplateSchema = templateBaseSchema.extend({
  template: batchTemplateFormSchema,
});

const templateSchema = z.union([activityTemplateSchema, batchTemplateSchema]);

export type ITemplateTag = z.infer<typeof templateTagSchema>;
export type IActivityTemplateWithPatient = z.infer<
  typeof activityTemplateWithPatientSchema
>;
type IActivityTemplateWithoutPatient = z.infer<
  typeof activityTemplateWithoutPatientSchema
>;
export type IActivityTemplate = z.infer<typeof activityTemplateSchema>;
export type IBatchTemplate = z.infer<typeof batchTemplateSchema>;
export type ITemplate = z.infer<typeof templateSchema>;

export { type IBatchTemplateActivity } from "@/forms/AddActivity/batchTemplateFormSchema";
export {
  batchTemplateActivityDelayedStartDateOptionsSchema,
  batchTemplateActivityDelayedStartDateUnitSchema,
  batchTemplateActivityInclusionTypeSchema,
  batchTemplateActivityTimeConfigTypeSchema,
} from "@/forms/AddActivity/batchTemplateFormSchema";

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * API endpoints TEMPLATES
 */

/**
 * Fetch all templates
 * @returns All templates grouped into activity templates and batch templates.
 * Also returns any malformed templates, so that they can be displayed to the user and dealt with accordingly.
 */
async function fetchTemplates(siteId: SiteId) {
  const backendTemplates = await logisticsApi
    .get(`/ambulating/templates${generateQueryString({ siteId })}`)
    .json(backendTemplateSchema.array());

  const parsedTemplates: z.infer<typeof templateSchema>[] = [];
  const malformedTemplates: z.infer<typeof backendTemplateSchema>[] = [];

  for (const backendTemplate of backendTemplates) {
    try {
      const parsedTemplate = templateSchema.safeParse({
        ...backendTemplate,
        template: JSON.parse(backendTemplate.template),
      });
      if (parsedTemplate.success) {
        parsedTemplates.push(parsedTemplate.data);
      } else {
        malformedTemplates.push(backendTemplate);
      }
    } catch {
      malformedTemplates.push(backendTemplate);

      // We don't expect JSON parse to fail, but if it does, we want to know about it.
      Sentry.captureException(
        new Error(
          `Failed to parse template: ${String(backendTemplate.templateId)}`,
        ),
      );
    }
  }

  const activities = parsedTemplates.filter(
    (template): template is IActivityTemplate =>
      !("activities" in template.template),
  );

  const activitiesWithPatient = activities.filter(
    (activity): activity is IActivityTemplateWithPatient =>
      "patientType" in activity.template,
  );
  const activitiesWithoutPatient = activities.filter(
    (activity): activity is IActivityTemplateWithoutPatient =>
      "withoutPatient" in activity.template,
  );

  const batches = parsedTemplates.filter(
    (template): template is IBatchTemplate => "activities" in template.template,
  );

  return {
    activities: {
      withPatient: activitiesWithPatient,
      withoutPatient: activitiesWithoutPatient,
    },
    batches,
    malformedTemplates,
  };
}

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * API endpoints TAGS
 */

async function fetchTags(siteId: SiteId) {
  return await logisticsApi
    .get(`/ambulating/tags${generateQueryString({ siteId })}`)
    .json(z.array(templateTagSchema));
}

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Queries
 */

export const templatesQueryOptions = (siteId: SiteId) =>
  queryOptions({
    queryKey: templatesKeys.forSite(siteId),
    queryFn: () => fetchTemplates(siteId),
  });

export const tagsByGroupQueryOptions = (siteId: SiteId, group: ITagGroup) =>
  queryOptions({
    queryKey: tagsKeys.group(siteId, group),
    queryFn: async () => {
      const tags = await fetchTags(siteId);
      return tags.filter((tag) => tag.displayName.startsWith(`${group}:`));
    },
  });
